// external
import { useCallback, useEffect, useContext, useMemo } from "react";

// state managing/security
import { ClientContext } from "context/ClientContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { FaoResultsContext } from "context/FaoResultsContext";

// transformers
import {
  transformApiData,
  createRegimeTabs,
  initializeUserInput,
} from "./admit-adjustment-transformers";

// api
import useAdmitAdjustmentApi from "./use-admit-adjustment-api";

/**
 * Custom hook for managing admit adjustment data loading
 * Optimized for performance and maintainability
 */
const useAdmitAdjustmentDataLoader = () => {
  const { currentClientData } = useContext(ClientContext);
  const { modelConfig } = useContext(FaoBuilderContext);
  const {
    setAdmitAdjustmentData,
    setAdmitAdjustmentTabs,
    setAdmitAdjustmentNeedRanks,
    setAdmitAdjustmentUserInput,
    setLoading,
    admitAdjustmentUserInput,
    setIsSaved,
  } = useContext(FaoResultsContext);
  
  const { fetchAllData } = useAdmitAdjustmentApi();

  /**
   * Memoized dependencies to prevent unnecessary effect runs
   */
  const dependencies = useMemo(() => ({
    modelId: modelConfig?.id,
    schoolKey: currentClientData?.schoolKey,
  }), [modelConfig?.id, currentClientData?.schoolKey]);

  /**
   * Reset saved state whenever admit adjustment user input changes
   */
  useEffect(() => {
    setIsSaved(prev => ({ ...prev, admitAdjustments: false }));
  }, [admitAdjustmentUserInput, setIsSaved]);

  /**
   * Memoized data transformation to prevent unnecessary recalculations
   */
  const transformData = useCallback((rawData) => {
    const transformedData = transformApiData(rawData);
    const tabs = createRegimeTabs(rawData);
    
    return { transformedData, tabs };
  }, []);

  /**
   * Main data loading effect
   * Only runs when modelId or schoolKey changes
   */
  useEffect(() => {
    const loadData = async () => {
      const { modelId, schoolKey } = dependencies;
      
      if (!modelId || !schoolKey) return;

      try {
        setLoading(prev => ({ ...prev, admitAdjustments: true }));

        // Fetch all data using the API hook
        const { needRanks, admitAdjustmentData } = await fetchAllData(schoolKey, modelId);

        const { transformedData, tabs } = transformData(admitAdjustmentData);
        
        // Batch state updates to prevent multiple re-renders
        setAdmitAdjustmentData(transformedData);
        setAdmitAdjustmentTabs(tabs);
        setAdmitAdjustmentNeedRanks(needRanks);
        setAdmitAdjustmentUserInput(initializeUserInput(transformedData));
      } catch (error) {
        console.error("Error loading admit adjustment data:", error);
        // Continue with empty states - let UI handle gracefully
      } finally {
        setLoading(prev => ({ ...prev, admitAdjustments: false }));
      }
    };

    loadData();
  }, [
    dependencies.modelId,
    dependencies.schoolKey,
    transformData,
    fetchAllData,
    setAdmitAdjustmentData,
    setAdmitAdjustmentTabs,
    setAdmitAdjustmentNeedRanks,
    setAdmitAdjustmentUserInput,
    setLoading,
  ]);
};

/**
 * Main hook that provides the data loading functionality
 * Clean and simple interface for components
 */
function useAdmitAdjustmentData() {
  useAdmitAdjustmentDataLoader();
}

export default useAdmitAdjustmentData;
